/**
 * Wire primitives fixed by the approved documents.
 *
 * Money  — integer minor units AS A STRING + currency (PR-0.1).
 * Rates  — integer basis points (PR-0.2).
 * Qty    — decimal AS A STRING to survive JSON number parsing (API_CONTRACT.md §1.2).
 *
 * Arithmetic lives in `@erm/money` (TASK-04 / DB-006). This file defines the
 * wire shape only and performs no calculation, but the shape defined here and
 * the shape `Money.toJSON()` emits are the same shape — one canonical wire
 * format, asserted by `primitives.test.ts`.
 */
import { z } from 'zod';
export const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, 'ISO-4217 alpha-3');
/**
 * Integer minor units, transported as a string.
 *
 * A JSON number decodes to an IEEE-754 double in every mainstream parser, and
 * `PR-0.1` prohibits floating point anywhere in the pricing or billing path
 * *including intermediate values*. Quoting the amount means no parser in the
 * chain can turn a paisa figure into a float before `@erm/money` converts it.
 * The grammar is deliberately strict: no leading zeros, no `+`, no exponent
 * notation, no decimal point — minor units are whole by definition.
 */
export const amountMinorSchema = z
    .string()
    .regex(/^-?(0|[1-9][0-9]*)$/, 'integer minor units as a string');
export const moneySchema = z.object({
    amount_minor: amountMinorSchema,
    currency: currencyCodeSchema,
    exponent: z.number().int().min(0).max(4),
});
/** Basis points: 1 bp = 0.01%. Integer only — never a float (PR-0.2). */
export const basisPointsSchema = z.number().int().min(0).max(1_000_000);
/** Decimal transported as a string, with its unit and precision (INV-22). */
export const quantitySchema = z.object({
    value: z.string().regex(/^-?\d+(\.\d+)?$/, 'decimal as string'),
    unit: z.string().min(1),
    precision: z.number().int().min(0).max(6),
});
/** UUID identifier used by every resource (DATABASE_SCHEMA.md §3.4). */
export const idSchema = z.string().uuid();
//# sourceMappingURL=primitives.js.map